/**
 * @file AttentionList.tsx
 * @description Блок «Требует внимания» на «Обзоре» платформы (phase19.29).
 *
 * Строки — только реальные признаки (`overview-model`): ошибки интеграций,
 * проблемы с копиями, а также источник, который не удалось загрузить (не
 * говорим «всё в порядке», если не смогли проверить). Строка — отдельная
 * плашка-ссылка в раздел высотой ≥ 56px с общим наведением
 * (`card-interactive`). Проблем нет — спокойное «Всё в порядке» со знаком
 * Commora.
 */

import { AlertCircle, ChevronRight, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import { Link } from "react-router";

import commoraMarkUrl from "../../../assets/brand/commora-mark.svg";
import { Skeleton } from "../../ui/skeleton";
import type { AttentionItem } from "./overview-model";
import { cn } from "@/lib/utils";

/** Значок и цвет строки по тону — реестром. */
const TONE_META: Record<AttentionItem["tone"], { icon: LucideIcon; className: string }> = {
  info: { icon: Info, className: "text-info" },
  warning: { icon: TriangleAlert, className: "text-warning" },
  error: { icon: AlertCircle, className: "text-destructive" },
};

interface Props {
  items: AttentionItem[];
  /** Хотя бы один источник ещё грузится. */
  loading: boolean;
  /** Что проверялось (по правам): «Всё в порядке» говорит только о проверенном. */
  checked: { integrations: boolean; backups: boolean };
}

/** Текст «всё в порядке» — только о разделах, которые проверялись. */
function allGoodText({ integrations, backups }: Props["checked"]): string {
  if (integrations && backups) return "Ошибок интеграций и проблем с резервными копиями нет.";
  if (integrations) return "Ошибок интеграций нет.";
  return "Проблем с резервными копиями нет.";
}

export function AttentionList({ items, loading, checked }: Props) {
  return (
    <section aria-labelledby="overview-attention" className="flex flex-col gap-3">
      <h2 id="overview-attention" className="type-section-title text-on-surface">
        Требует внимания
      </h2>
      {loading ? (
        <div className="flex flex-col gap-3 rounded-xl bg-card p-4 ring-1 ring-outline-variant" aria-label="Загрузка">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
        </div>
      ) : items.length === 0 ? (
        <AllGood text={allGoodText(checked)} />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <AttentionRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  const meta = TONE_META[item.tone];
  const Icon = meta.icon;
  return (
    <li>
      <Link
        to={item.to}
        className="card-interactive flex min-h-14 items-center gap-3 rounded-xl bg-card px-4 py-3 text-sm text-on-surface ring-1 ring-outline-variant outline-none"
      >
        <Icon className={cn("size-4 shrink-0", meta.className)} aria-hidden />
        <span className="min-w-0 flex-1">{item.text}</span>
        <span className="hidden shrink-0 text-primary sm:inline">Открыть</span>
        <ChevronRight className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
      </Link>
    </li>
  );
}

/** Пустое состояние: проблем не найдено. */
function AllGood({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-[linear-gradient(135deg,var(--accent)_0%,var(--card)_70%)] px-4 py-5 ring-1 ring-outline-variant">
      <img src={commoraMarkUrl} alt="" aria-hidden width={32} height={32} className="shrink-0" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold text-on-surface">Всё в порядке</span>
        <span className="text-sm text-on-surface-variant">{text}</span>
      </div>
    </div>
  );
}
